import React from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowLeft, ReadCvLogo, X, type Icon } from "@phosphor-icons/react";
import OverrideChip from "./OverrideChip";

interface ConfirmGenerationStepProps {
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  /** Full name of the client, displayed in the subtitle and document card */
  clientName: string;
  /** Display title of the document type (e.g. "Initial Treatment Plan") */
  documentTitle: string;
  /** Description shown in the card body explaining what will be generated */
  documentDescription: string;
  /** Phosphor icon component for the document type */
  documentIcon?: Icon;
  /** Callback to return to document type selection */
  onBack: () => void;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when the user confirms generation */
  onGenerate: () => void;
  /** Whether generation is currently in progress */
  isGenerating: boolean;
}

/**
 * Confirmation step of the Generate Document dialog.
 * Shown when all required data is available for the selected document type.
 * Displays a summary card and prompts the user to confirm generation.
 *
 * @param props
 * @param props.clientName - Full name of the client
 * @param props.documentTitle - Display title of the document type
 * @param props.documentDescription - Description of what will be generated
 * @param props.documentIcon - Phosphor icon component for the document type
 * @param props.onBack - Callback to return to document type selection
 * @param props.onClose - Callback to close the dialog
 * @param props.onGenerate - Callback when the user confirms generation
 */
const ConfirmGenerationStep: React.FC<ConfirmGenerationStepProps> = ({
  clientName,
  documentTitle,
  documentDescription,
  documentIcon: DocumentIcon = ReadCvLogo,
  onBack,
  onClose,
  onGenerate,
  isGenerating,
  isOverridden = false,
}) => {
  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={onBack}
            disabled={isGenerating}
            aria-label="Back to document selection"
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "10px",
              boxShadow: 1,
              p: "5px",
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h2">
              Confirm Generation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ready to generate for {clientName}.
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          disabled={isGenerating}
          aria-label="Close dialog"
          size="small"
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pb: 7, pt: 0 }}>
        {isOverridden && (
          <Stack sx={{ mt: 7 }}>
            <OverrideChip />
          </Stack>
        )}
        <Card variant="outlined" sx={{ borderRadius: 4, mt: 7, backgroundColor: "grey.50" }}>
          <CardHeader
            sx={{ pb: 0 }}
            titleTypographyProps={{ variant: "body1" }}
            subheaderTypographyProps={{ variant: "body2" }}
            avatar={
              <Avatar sx={{ bgcolor: "primary.background", width: 32, height: 32 }}>
                <Box sx={{ color: "primary.main", display: "flex" }}>
                  <DocumentIcon size={20} weight="duotone" />
                </Box>
              </Avatar>
            }
            title={documentTitle}
            subheader={clientName}
          />
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              {documentDescription}
            </Typography>
          </CardContent>
        </Card>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3 }}>
          Keep this window open until it&apos;s ready — this can take up to 2 minutes. If you leave
          or close it, generation will stop and you&apos;ll need to start over.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5 }}>
        <LoadingButton
          variant="contained"
          color="primary"
          fullWidth
          onClick={onGenerate}
          loading={isGenerating}
          aria-label={`Generate ${documentTitle}`}
        >
          Generate
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default ConfirmGenerationStep;
